// @ts-nocheck
import React, { useEffect } from "react";
import { Switch, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "react-oidc-context";
import App from "./App";
import EmailEnter from "./EmailEnter";
import { Loading } from "@qjumpers/common-ui";
import environmentConfig from "constants/config";
import { JL } from "jsnlog";
import { setTokenProvider } from "./middleware/api";
import { setupClientLogger } from "./ClientLogger";

const AUTH_FLAG_KEY = "userWasAuthenticated";
const INTENDED_PATH_KEY = "intendedPath";

const authFlags = {
  set: () => localStorage.setItem(AUTH_FLAG_KEY, "true"),
  get: () => localStorage.getItem(AUTH_FLAG_KEY) === "true",
  clear: () => localStorage.removeItem(AUTH_FLAG_KEY),
};

const pathManager = {
  save: (path) => {
    if (path !== "/" && path !== "/callback") {
      sessionStorage.setItem(INTENDED_PATH_KEY, path);
    }
  },
  get: () => sessionStorage.getItem(INTENDED_PATH_KEY),
  clear: () => sessionStorage.removeItem(INTENDED_PATH_KEY),
  getCurrentPath: () => window.location.pathname + window.location.search,
};

 const CenteredLoading = ({ message = "Loading..." }: { message?: string }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      width: "100vw",
      position: "fixed",
      top: 0,
      left: 0,
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      zIndex: 9999,
    }}>
    <Loading className="loading-component" />
    <div style={{ marginTop: "20px", color: "#666" }}>{message}</div>
  </div>
);

const LOADING_MESSAGES = {
  redirecting: "Redirecting...",
  signinRedirect: "Authenticating...",
  signinSilent: "Authenticating...",
  signinCallback: "Authenticating...",
  signoutRedirect: "Signing out...",
  signoutSilent: "Signing out...",
};

const getAuthLoadingMessage = (auth) => {
  if (!auth.isLoading) return null;
  if (auth.activeNavigator) {
    return LOADING_MESSAGES[auth.activeNavigator] || LOADING_MESSAGES.signinRedirect;
  }
  return LOADING_MESSAGES.signinRedirect;
};

const TokenProvider = ({ children }: { children: React.ReactNode }) => {
  const auth = useAuth();

  useEffect(() => {
    setTokenProvider(() => {
      if (!auth?.user) return null;

      return {
        access_token: auth.user.access_token,
        expires_at: auth.user.expires_at,
        renew: async () => {
          await auth.signinSilent?.();
          return auth.user;
        },
        logout: async (options = {}) => {
          try {
            authFlags.clear();
            if (options.silent && auth.signoutSilent) {
              await auth.signoutSilent();
            } else {
              await auth.signoutRedirect();
            }
          } catch (error) {
            // eslint-disable-next-line no-console
          }
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user, auth.signinSilent]);

  return children;
};

const createOIDCHandlers = () => ({
  onSigninCallback: (_user) => {
    authFlags.set();
    sessionStorage.setItem("lowCreditPopupShown", "false");
    setupClientLogger();
    window.history.replaceState({}, document.title, window.location.pathname);
  },

  onSignoutCallback: () => {
    authFlags.clear();
    sessionStorage.clear();
  },

  onUserLoaded: (_user) => {
    authFlags.set();
    setupClientLogger();
  },

  onAccessTokenExpired: () => { /* do nothing */ },

  onAccessTokenExpiring: () => { /* do nothing */ },

  onSilentRenewError: (_error) => {
    // eslint-disable-next-line no-console
  },

  onUserSignedOut: () => {
    authFlags.clear();
    sessionStorage.clear();
  },
});

const createOIDCConfig = (props: { idp?: string; email?: string }) => ({
  authority: `${environmentConfig.KEYCLOAK.apiHost}${environmentConfig.KEYCLOAK.apiPath}/realms/${environmentConfig?.realm}/`,
  client_id: environmentConfig.clientId,
  redirect_uri: window.location.origin,
  response_type: "code",
  post_logout_redirect_uri: window.location.origin,
  automaticSilentRenew: true,
  silent_redirect_uri: `${window.location.origin}/silent-renew.html`,
  loadUserInfo: true,
  response_mode: "query",
  code_challenge_method: "S256",
  extraQueryParams: props.idp
    ? {
      kc_idp_hint: props.idp,
      login_hint: props.email,
    }
    : {},
});

const AuthenticatedApp = () => {
  const auth = useAuth();

  if (auth.isLoading) {
    return <CenteredLoading message={getAuthLoadingMessage(auth)} />;
  }

  if (auth.error) {
    if (auth.error?.source !== "signinSilent") {
      auth.signinSilent().catch(() => {
        auth.removeUser().then(() => auth.signinRedirect());
      });
    } else {
      auth.removeUser().then(() => auth.signinRedirect());
    }

    return <CenteredLoading message={LOADING_MESSAGES.signinRedirect} />;
  }

  return (
    <Switch>
      <Route path="/" component={MainApp} />
    </Switch>
  );
};

const MainApp = () => {
  const auth = useAuth();

  useEffect(() => {
    if (!auth?.isAuthenticated && authFlags.get()) {
      pathManager.save(pathManager.getCurrentPath());
      auth.signinRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user?.access_token, auth.isAuthenticated]);

  if ((!auth.isAuthenticated && !auth.isLoading) || !authFlags.get()) {
    pathManager.save(pathManager.getCurrentPath());
    return <EmailEnter />;
  }

  const intendedPath = pathManager.get();
  const currentPath = pathManager.getCurrentPath();

  if (intendedPath && intendedPath !== "/" && intendedPath !== "/callback" && intendedPath !== currentPath) {
    pathManager.clear();
    window.location.replace(intendedPath);
    return <CenteredLoading message={LOADING_MESSAGES.redirecting} />;
  }

  return <App />;
};

const AuthRouter = (props: { idp?: string; email?: string }) => {
  const oidcConfig = createOIDCConfig(props);
  const oidcHandlers = createOIDCHandlers();

  return (
    <AuthProvider {...oidcConfig} {...oidcHandlers}>
      <TokenProvider>
        <AuthenticatedApp />
      </TokenProvider>
    </AuthProvider>
  );
};

export default AuthRouter;